/**
 * # Branded ids
 *
 * The ids a lona server mints for users, auth accounts, calendars, events,
 * tasks, cells, sync jobs, scheduling slots and template rows, each its own type: a `string` at runtime, but an `EventId` is not
 * accepted where a `TaskId` (or any `string`) is expected, so an id of one
 * kind cannot be passed for another by accident.
 *
 * Each has two constructors:
 *
 * - `X.parse(raw)` — the checked one: `raw` as an `X`, or `null` when it is
 *   not one (an empty string). Use it where an id enters from outside the
 *   SDK's typed surface (a URL, user input, a stored preference).
 * - `X.of(raw)` — the throwing one, for a value already known to be an id of
 *   that kind (a test fixture, a literal). It throws where `parse` refuses.
 *
 * The SDK's decoders brand the ids they read, so an id taken from a decoded
 * value (`event.id`, `task.id`, `calendar.authId`) needs no constructor.
 *
 * `AuthId` and `UserId` are the same types the generated wire models write
 * (`Network.AuthId`, `Network.UserId`): the brands are structural, so either
 * spelling is accepted where the other is expected.
 */
import type { Option } from "../core/option.ts";
import { Uuid } from "@tento-core/uuid";
import type { RowId } from "./row-id.ts";
import type { SheetId } from "./sheet-id.ts";

/** A lona user's id, a branded string (`UserId.of(email)`); `UserId.SYSTEM`, `LOCAL` and `DETACHED` own the rows no user does. */
export type UserId = string & { readonly __brand: "UserId" };

/** A lona user's id (`parse` / `of`). */
export namespace UserId {
  /** `raw` as a `UserId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<UserId> {
    return raw.length > 0 ? (raw as UserId) : null;
  }

  /** `raw` as a `UserId`; throws when `parse` refuses it. */
  export function of(raw: string): UserId {
    return orThrow("UserId", raw, parse(raw));
  }

  /**
   * The owners the SDK stamps on a row no user owns — none is an account,
   * and none is ever sent as one: a system row the sheet builds (header,
   * footer, second header: `SYSTEM`), a pending row not yet persisted
   * (`LOCAL`), a detached or ephemeral seed row (`DETACHED`). Each is a
   * `UserId` its own `parse` accepts, so a row copied from one (a child
   * inheriting its parent's owner) re-brands without a throw.
   */
  export const SYSTEM = "system" as UserId;
  /** See `SYSTEM`: a pending row's owner. */
  export const LOCAL = "local" as UserId;
  /** See `SYSTEM`: a detached or ephemeral seed row's owner. */
  export const DETACHED = "detached" as UserId;
}

/** An auth account's id: the provider account (a Google login) a calendar or event belongs to. */
export type AuthId = string & { readonly __brand: "AuthId" };

/** An auth account's id (`parse` / `of`). */
export namespace AuthId {
  /** `raw` as an `AuthId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<AuthId> {
    return raw.length > 0 ? (raw as AuthId) : null;
  }

  /** `raw` as an `AuthId`; throws when `parse` refuses it. */
  export function of(raw: string): AuthId {
    return orThrow("AuthId", raw, parse(raw));
  }
}

/** A calendar's id on its provider, unique within one auth account (`AuthId`). */
export type CalendarId = string & { readonly __brand: "CalendarId" };

/** A calendar's id (`parse` / `of`). */
export namespace CalendarId {
  /** `raw` as a `CalendarId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<CalendarId> {
    return raw.length > 0 ? (raw as CalendarId) : null;
  }

  /** `raw` as a `CalendarId`; throws when `parse` refuses it. */
  export function of(raw: string): CalendarId {
    return orThrow("CalendarId", raw, parse(raw));
  }
}

/** An event's id (a server id, or the placeholder id of an optimistic event). */
export type EventId = string & { readonly __brand: "EventId" };

/** An event's id (`parse` / `of`). */
export namespace EventId {
  /** `raw` as an `EventId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<EventId> {
    return raw.length > 0 ? (raw as EventId) : null;
  }

  /** `raw` as an `EventId`; throws when `parse` refuses it. */
  export function of(raw: string): EventId {
    return orThrow("EventId", raw, parse(raw));
  }
}

/** A task's id (`Network.Task.id`): a branded string, minted with `TaskId.of` / `TaskId.parse`. */
export type TaskId = string & { readonly __brand: "TaskId" };

/** A task's id (`parse` / `of`). */
export namespace TaskId {
  /** `raw` as a `TaskId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<TaskId> {
    return raw.length > 0 ? (raw as TaskId) : null;
  }

  /** `raw` as a `TaskId`; throws when `parse` refuses it. */
  export function of(raw: string): TaskId {
    return orThrow("TaskId", raw, parse(raw));
  }
}

/**
 * The correlation id of one pending mutation in an optimistic overlay
 * (`PendingMutation.clientId`): the label the writer gives an entry when it
 * appends it (`opt-1`, `occ-3`), which the backend's ack and the overlay's
 * `confirm` / `ack` / `drop` / `expire` name it by. Not a server id: it is
 * never the item's own id (`ConfirmResult.id`), and unique only within the
 * overlay that holds it.
 */
export type MutationClientId = string & { readonly __brand: "MutationClientId" };

/** A pending mutation's correlation id (`parse` / `of`). */
export namespace MutationClientId {
  /** `raw` as a `MutationClientId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<MutationClientId> {
    return raw.length > 0 ? (raw as MutationClientId) : null;
  }

  /** `raw` as a `MutationClientId`; throws when `parse` refuses it. */
  export function of(raw: string): MutationClientId {
    return orThrow("MutationClientId", raw, parse(raw));
  }
}

/**
 * A cell's id: the server-minted key of one cell in a `multi`-cardinality
 * row (a sticker, an availability slot, a timepoint cell) — what a clear, a
 * replace (`replaceCellId`) or a keyed write (`CellTarget.keyed`) names. A
 * `single`-cardinality row's cells are keyed by date alone and carry none.
 */
export type CellId = string & { readonly __brand: "CellId" };

/** A cell's id (`parse` / `of`). */
export namespace CellId {
  /** `raw` as a `CellId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<CellId> {
    return raw.length > 0 ? (raw as CellId) : null;
  }

  /** `raw` as a `CellId`; throws when `parse` refuses it. */
  export function of(raw: string): CellId {
    return orThrow("CellId", raw, parse(raw));
  }
}

/**
 * A sheet's id as the server's create and list routes spell it: a bare UUID
 * (`Network.Sheet.id`, `NewRow.sheetId`) — not the SDK's interned `SheetId`
 * (`ss_<hex>`), which the server's strict `uuid` fields refuse.
 * `SheetUuid.fromId(sheetId)` spells a `SheetId`; `parse(raw)` checks a string.
 */
export type SheetUuid = Uuid & { readonly __brand: "SheetUuid" };
/** A sheet's bare UUID (`parse` / `fromWire` / `make` / `of`). */
export namespace SheetUuid {
  /**
   * `raw` as a `SheetUuid`, or `null` for an empty string. The UUID shape is not
   * checked here (as `Uuid.parse` does not check it): a sheet or row the SDK
   * opened under another spelling still decodes (a Known gap).
   */
  export function parse(raw: string): Option<SheetUuid> {
    return raw.length > 0 ? (raw as SheetUuid) : null;
  }

  /** `raw` as a `SheetUuid`; throws when `parse` refuses it. */
  export function fromWire(raw: string): SheetUuid {
    return orThrow("SheetUuid", raw, parse(raw));
  }

  /** A fresh random `SheetUuid` (a client-minted sheet id). */
  export function make(): SheetUuid {
    return fromWire(Uuid.make());
  }

  /** `id`'s UUID, or `null` for an id with none (an `ss_` key that is not 32 hex digits). */
  export function fromId(id: SheetId): Option<SheetUuid> {
    const uuid = id.toUuid();
    return uuid == null ? null : parse(uuid);
  }
}

/**
 * A row's id as the server's create route spells it: a bare UUID
 * (`NewRow.id` / `parentId`, `CreatedRow.id`) — not the SDK's interned
 * `RowId` (`r_<hex>`). `RowUuid.fromId(rowId)` spells a user row's `RowId`.
 */
export type RowUuid = Uuid & { readonly __brand: "RowUuid" };
/** A row's bare UUID (`parse` / `fromWire` / `make` / `of`). */
export namespace RowUuid {
  /**
   * `raw` as a `RowUuid`, or `null` for an empty string. The UUID shape is not
   * checked here (as `Uuid.parse` does not check it): a sheet or row the SDK
   * opened under another spelling still decodes (a Known gap).
   */
  export function parse(raw: string): Option<RowUuid> {
    return raw.length > 0 ? (raw as RowUuid) : null;
  }

  /** `raw` as a `RowUuid`; throws when `parse` refuses it. */
  export function fromWire(raw: string): RowUuid {
    return orThrow("RowUuid", raw, parse(raw));
  }

  /** A fresh random `RowUuid` (a client-minted row id). */
  export function make(): RowUuid {
    return fromWire(Uuid.make());
  }

  /** `id`'s UUID, or `null` for a row with none (a lookup, system or virtual row). */
  export function fromId(id: RowId): Option<RowUuid> {
    const uuid = id.toUuid();
    return uuid == null ? null : parse(uuid);
  }
}

/**
 * A server sync job's key: what a read answers while a background sync of a
 * row is in flight (`syncKeys`), and what `/api/sync/{syncKey}/status`
 * polls. Opaque — the server mints it and the SDK only hands it back — but
 * not any string: a row key or a lookup key is not one.
 */
export type SyncKey = string & { readonly __brand: "SyncKey" };

/** A server sync job's key (`parse` / `of`). */
export namespace SyncKey {
  /** `raw` as a `SyncKey`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<SyncKey> {
    return raw.length > 0 ? (raw as SyncKey) : null;
  }

  /** `raw` as a `SyncKey`; throws when `parse` refuses it. */
  export function of(raw: string): SyncKey {
    return orThrow("SyncKey", raw, parse(raw));
  }
}

/**
 * A scheduling slot's id: the key of one bookable slot of a scheduling
 * link's `slot.v1` row, what a booking names.
 */
export type SlotId = string & { readonly __brand: "SlotId" };

/** A scheduling slot's id (`parse` / `of`). */
export namespace SlotId {
  /** `raw` as a `SlotId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<SlotId> {
    return raw.length > 0 ? (raw as SlotId) : null;
  }

  /** `raw` as a `SlotId`; throws when `parse` refuses it. */
  export function of(raw: string): SlotId {
    return orThrow("SlotId", raw, parse(raw));
  }
}

/**
 * A template row's id within its template (`TemplateRowDef.id`): unique in
 * one template, not a row of any sheet — a preview row's `RowId` is derived
 * from it (`RowId.deterministic`).
 */
export type TemplateRowId = string & { readonly __brand: "TemplateRowId" };

/** A template row's id (`parse` / `of`). */
export namespace TemplateRowId {
  /** `raw` as a `TemplateRowId`, or `null` when it is not one (an empty string). */
  export function parse(raw: string): Option<TemplateRowId> {
    return raw.length > 0 ? (raw as TemplateRowId) : null;
  }

  /** `raw` as a `TemplateRowId`; throws when `parse` refuses it. */
  export function of(raw: string): TemplateRowId {
    return orThrow("TemplateRowId", raw, parse(raw));
  }
}

function orThrow<T>(kind: string, raw: string, id: Option<T>): T {
  if (id == null) throw new Error(kind + ".of: not a " + kind + ": " + JSON.stringify(raw));
  return id;
}
